import React, { useState, useEffect, useMemo } from "react";
import "../Overview/style.css";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Card, CardContent, IconButton } from "@mui/material";
import OverViewChart from "../../../components/dashboardcomponents/OverViewChart";
import BarChart from "../../../components/dashboardcomponents/Home/BarChart";
import MainHeaderAnalytics from "../../../components/dashboardcomponents/Analytics/MainHeaderAnalytics";
import dayjs from "dayjs";
import TabNavigation from "../../../components/dashboardcomponents/Home/TabNavigation";
import AgentsHeatmap from "../../../components/dashboardcomponents/Agents/AgentsHeatMap";
import EastIcon from "@mui/icons-material/East";

import {
  getAnalyticsAgentsStats,
  initAnalyticsAgentsStats,
  setDateRange,
} from "../../../store/features/geAnalyticsAgentsStats";
import {
  getAgentsChatsStats,
  initAgentsChatsStats,
} from "../../../store/features/getAgentsChatsStats";
import {
  getAgentsTicketsStats,
  initAgentsTicketsStats,
} from "../../../store/features/getAgentsTicketStats";
import {
  getAgentsCsatStats,
  initAgentsCsatStats,
} from "../../../store/features/getAgentsCsatStats";
import {
  getAgentsAvailabilityStats,
  initAgentsAvailabilityStats,
} from "../../../store/features/getAgentsAvailability";
import { useAnalyticsExport } from "../../../hooks/useAnalyticsExport";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";

// ==================== TRANSFORM FUNCTIONS (UNCHANGED) ====================
const transformAgentsChatsData = (apiData, agentKeyMap) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  return apiData.map(period => {
    const dataPoint = { day: period.label };
    agentKeyMap.forEach((key) => { dataPoint[key] = 0; });
    period.topAgents?.forEach(agent => {
      const key = agentKeyMap.get(agent.agent_id);
      if (key) dataPoint[key] = agent.chatsHandled || 0;
    });
    return dataPoint;
  });
};

const transformAgentsTicketsData = (apiData, agentKeyMap) => {
  if (!apiData || !Array.isArray(apiData)) return [];
  return apiData.map(period => {
    const dataPoint = { day: period.label };
    agentKeyMap.forEach((key) => { dataPoint[key] = 0; });
    period.topAgents?.forEach(agent => {
      const key = agentKeyMap.get(agent.agent_id);
      if (key) dataPoint[key] = agent.ticketsHandled || 0;
    });
    return dataPoint;
  });
};

const buildEmptyAvailabilityGrid = (periods, numGroupsExpected, isMonthly = false) => {
  const numHours = periods.length;
  const cols = Math.max(1, numGroupsExpected || 1);
  const matrix = Array.from({ length: numHours }, () => Array(cols).fill('veryLow'));
  const result = { hours: periods, matrix };
  if (isMonthly) {
    result.months = Array.from({ length: cols }, () => '-');
  } else {
    result.days = cols === 1 ? ['Selected Day'] : Array.from({ length: cols }, () => '-');
  }
  return result;
};

const transformAvailabilityData = (apiResponse, numGroupsExpected, isMonthly = false) => {
  const apiData = apiResponse?.data?.data || apiResponse?.data;
  const periods = ['12am', '3am', '6am', '9am', '12pm', '3pm', '6pm', '9pm'];
  if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
    return buildEmptyAvailabilityGrid(periods, numGroupsExpected, isMonthly);
  }

  const numHours = periods.length;

  if (numGroupsExpected === 1 && apiData.length === numHours) {
    const matrix = apiData.map(item => {
      const count = item.agentCount || 0;
      let level = count === 0 ? 'veryLow' : count <= 2 ? 'low' : count <= 5 ? 'medium' : 'high';
      return [level];
    });
    return { hours: periods, matrix, days: [apiData[0]?.day || 'Today'] };
  }

  const actualDataLength = apiData.length;
  if (actualDataLength % numHours !== 0) {
    console.warn(`Availability data length ${actualDataLength} not divisible by ${numHours}`);
    return buildEmptyAvailabilityGrid(periods, numGroupsExpected, isMonthly);
  }

  const numGroups = actualDataLength / numHours;
  const firstItem = apiData[0];
  if (!firstItem) {
    return buildEmptyAvailabilityGrid(periods, numGroupsExpected, isMonthly);
  }
  const labelKey = firstItem.month ? 'month' : firstItem.date ? 'date' : 'day';  const dateLabels = [];
  for (let i = 0; i < numGroups; i++) {
    const item = apiData[i * numHours];
    dateLabels.push(item?.[labelKey] || `Group ${i + 1}`);
  }

  const matrix = [];
  for (let hourIdx = 0; hourIdx < numHours; hourIdx++) {
    const row = [];
    for (let groupIdx = 0; groupIdx < numGroups; groupIdx++) {
      const item = apiData[groupIdx * numHours + hourIdx];
      const count = item?.agentCount || 0;
      const level = count === 0 ? 'veryLow' : count <= 2 ? 'low' : count <= 5 ? 'medium' : 'high';
      row.push(level);
    }
    matrix.push(row);
  }

  const result = { hours: periods, matrix };
  if (labelKey === 'month') result.months = dateLabels;
  else if (labelKey === 'date') result.dayGroups = dateLabels;
  else result.days = dateLabels;
  return result;
};

const attachAgentCounts = (transformedData, apiResponse) => {
  const apiData = apiResponse?.data?.data || apiResponse?.data;
  return apiData && Array.isArray(apiData) ? { ...transformedData, agentCounts: apiData } : transformedData;
};

// ==================== MAIN COMPONENT ====================
function MainAnalyticsAgents() {
  const [activeTab, setActiveTab] = useState("Agents Chats");
  const [csatAgentFilter, setCsatAgentFilter] = useState("All Agents");
  const [csatAgentOptions, setCsatAgentOptions] = useState([{ id: "all", name: "All Agents" }]);
  const [csatMonthPage, setCsatMonthPage] = useState(0); // 0 = Jan-Jun, 1 = Jul-Dec

  const dispatch = useDispatch();

  const {
    data: analyticsAgentsData,
    currentRange,
    dateRange,
    loading: agentsOverviewLoading,
  } = useSelector((state) => state.agentAnalyticsStats);

  const agentsChatsLoading = useSelector((state) => state.agentsChatsStats?.loading);
  const agentsTicketsLoading = useSelector((state) => state.agentsTicketsStats?.loading);

  const dataLoading = useMemo(
    () =>
      !!(agentsOverviewLoading || agentsChatsLoading || agentsTicketsLoading),
    [agentsOverviewLoading, agentsChatsLoading, agentsTicketsLoading]
  );

  const agentsChatsData = useSelector((state) => state.agentsChatsStats?.data);
  const agentsTicketsData = useSelector((state) => state.agentsTicketsStats?.data);
  const agentsCsatData = useSelector((state) => state.agentsCsatStats?.data);
  const csatLoading = useSelector((state) => state.agentsCsatStats?.loading);
  const agentsAvailabilityData = useSelector((state) => state.agentsAvailabilityStats?.data);
  const availabilityLoading = useSelector((state) => state.agentsAvailabilityStats?.loading);

  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();

  const {
    exportSectionOptions,
    exportSection,
    setExportSection,
    handleExport,
    exportLoading,
  } = useAnalyticsExport("agent", currentRange, dateRange, selectedGroupId);

  // === Time filter detection ===
  const activeTimeFilter = useMemo(() => {
    switch (currentRange) {
      case "today": return "today";
      case "yesterday": return "yesterday";
      case "7d": return "7 days";
      case "30d": return "30 days";
      case "1y": return "12 months";
      case "custom": return "custom";
      default: return "7 days";
    }
  }, [currentRange]);

  const is12MonthView = activeTimeFilter === "12 months";

  // Reset page when leaving 12-month view
  useEffect(() => {
    if (!is12MonthView) setCsatMonthPage(0);
  }, [is12MonthView]);

  // === Global agents & colors ===
  const globalAgents = useMemo(() => {
    const map = new Map();
    [agentsChatsData, agentsTicketsData, agentsCsatData].forEach(source => {
      if (!source?.data) return;
      source.data.forEach(period => {
        (period.topAgents || period.agents || []).forEach(agent => {
          if (agent.agent_id && agent.agent_id !== "unknown") {
            map.set(agent.agent_id, {
              id: agent.agent_id,
              name: agent.name || "Unnamed Agent",
              email: agent.email || "",
            });
          }
        });
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [agentsChatsData, agentsTicketsData, agentsCsatData]);

  const agentKeyMap = useMemo(() => {
    const map = new Map();
    globalAgents.forEach((agent, i) => map.set(agent.id, `agent${i + 1}`));
    return map;
  }, [globalAgents]);

  const globalAgentNames = useMemo(() => globalAgents.map(a => a.name), [globalAgents]);

  const nameToColor = useMemo(() => {
    const names = [...new Set(globalAgents.map(a => a.name.toLowerCase()))].sort();
    const colors = ["#148DFD", "#FA8C16", "#52C41A", "#9254DE", "#5CDBD3", "#FF4D4F", "#13C2C2", "#722ED1"];
    const map = {};
    names.forEach((name, i) => { map[name] = colors[i % colors.length]; });
    return map;
  }, [globalAgents]);

  // === CSAT Agent Options ===
  useEffect(() => {
    if (!agentsCsatData?.data || agentsCsatData.data.length === 0) {
      setCsatAgentOptions([{ id: "all", name: "All Agents" }]);
      setCsatAgentFilter("All Agents");
      return;
    }

    const map = new Map();
    agentsCsatData.data.forEach(period => {
      (period.agents || []).forEach(agent => {
        if (agent.agent_id && agent.name && agent.agent_id !== "unknown") {
          map.set(String(agent.agent_id), {
            id: String(agent.agent_id),
            name: agent.name.trim(),
            email: agent.email || "",
          });
        }
      });
    });

    const sorted = Array.from(map.values()).sort((a, b) => {
      const idxA = globalAgents.findIndex(g => g.id === a.id || g.name.toLowerCase() === a.name.toLowerCase());
      const idxB = globalAgents.findIndex(g => g.id === b.id || g.name.toLowerCase() === b.name.toLowerCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });

    const options = [{ id: "all", name: "All Agents" }, ...sorted];
    setCsatAgentOptions(options);
    if (!options.some(o => o.name === csatAgentFilter)) {
      setCsatAgentFilter("All Agents");
    }
  }, [agentsCsatData, globalAgents, csatAgentFilter]);

  // ✅ FIXED: Compute overall CSAT has data across all periods based on AGENT PRESENCE (not csat > 0)
  const overallCsatHasData = useMemo(() => {
    if (!agentsCsatData?.data || !Array.isArray(agentsCsatData.data)) return false;

    const selectedAgentId = csatAgentOptions.find(a => a.name === csatAgentFilter)?.id !== "all"
      ? csatAgentOptions.find(a => a.name === csatAgentFilter)?.id
      : null;

    if (selectedAgentId) {
      // Single agent: check if any period has the agent present (even if csat=0)
      return agentsCsatData.data.some(period => {
        const agent = period.agents?.find(a => String(a.agent_id) === selectedAgentId);
        return !!agent;  // ✅ FIXED: Presence only
      });
    } else {
      // All agents: check if any period has any agent present
      return agentsCsatData.data.some(period =>
        period.agents?.length > 0  // ✅ FIXED: Any agents present
      );
    }
  }, [agentsCsatData, csatAgentFilter, csatAgentOptions]);

  // Initial load when group is selected
  useEffect(() => {
    if (!selectedGroupId) return;

    Promise.all([
      dispatch(initAgentsChatsStats({ group_id: selectedGroupId })),
      dispatch(initAgentsTicketsStats({ group_id: selectedGroupId })),
      dispatch(initAnalyticsAgentsStats({ group_id: selectedGroupId })),
      dispatch(initAgentsCsatStats({ group_id: selectedGroupId })),
      dispatch(initAgentsAvailabilityStats({ group_id: selectedGroupId })),
    ]);
  }, [dispatch, selectedGroupId]);

  const csatAvailableAgents = useMemo(() => csatAgentOptions.filter(o => o.id !== "all"), [csatAgentOptions]);
  const singleCsatAgentColor = useMemo(() => {
    return csatAgentFilter === "All Agents" ? null : nameToColor[csatAgentFilter.toLowerCase()] || "#148DFD";
  }, [csatAgentFilter, nameToColor]);

  // === CSAT CHART DATA - THIS IS THE FIX ===
  const getCsatChartData = useMemo(() => {
    if (!agentsCsatData?.data || !Array.isArray(agentsCsatData.data)) return [];

    const selectedAgentId = csatAgentOptions.find(a => a.name === csatAgentFilter)?.id !== "all"
      ? csatAgentOptions.find(a => a.name === csatAgentFilter)?.id
      : null;

    let data = agentsCsatData.data.map(period => {
      if (selectedAgentId) {
        const agent = period.agents?.find(a => String(a.agent_id) === selectedAgentId);
        return {
          day: period.label || "—",
          value: agent?.csat || 0,
          hasAgentData: !!agent  // ✅ NEW: Flag for agent presence (even if csat=0)
        };
      }

      // ✅ UPDATED: For All Agents, init to null, set only for present agents
      const point = { day: period.label || "—" };
      csatAvailableAgents.forEach((_, i) => point[`agent${i + 1}`] = null);
      period.agents?.forEach(agent => {
        const idx = csatAvailableAgents.findIndex(a => String(a.id) === String(agent.agent_id));
        if (idx !== -1) point[`agent${idx + 1}`] = agent.csat || 0;
      });
      return point;
    });

    // ALWAYS paginate in 12-month view - THIS IS THE KEY FIX
    if (is12MonthView) {
      const start = csatMonthPage * 6;
      data = data.slice(start, start + 6);
    }

    return selectedAgentId
      ? data.map(d => ({ day: d.day, value: d.value, hasAgentData: d.hasAgentData }))  // ✅ Pass hasAgentData
      : data;
  }, [agentsCsatData, csatAgentFilter, csatAgentOptions, csatAvailableAgents, is12MonthView, csatMonthPage]);

  // Arrow controls
  const handlePrev = () => setCsatMonthPage(0);
  const handleNext = () => setCsatMonthPage(1);
  const canPrev = is12MonthView && csatMonthPage > 0;
  const canNext = is12MonthView && csatMonthPage < 1;

  const csatAgentNames = useMemo(() => {
    return csatAgentFilter === "All Agents"
      ? csatAvailableAgents.map(a => a.name)
      : [csatAgentFilter];
  }, [csatAvailableAgents, csatAgentFilter]);

  // Date change handler
  const handleCsatAgentFilterChange = (newFilter) => {
    setCsatAgentFilter(newFilter);

    const selectedAgent = csatAgentOptions.find(a => a.name === newFilter);
    const agentId = selectedAgent?.id !== 'all' ? String(selectedAgent?.id) : undefined;

    dispatch(getAgentsCsatStats({
      range: currentRange,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      agent_id: agentId,
      group_id: selectedGroupId,
    }));
  };

  const handleDateChange = async (newDateRange) => {
    if (!newDateRange?.startDate || !newDateRange?.endDate) return;

    await dispatch(setDateRange({
      startDate: newDateRange.startDate,
      endDate: newDateRange.endDate,
    }));

    setCsatAgentFilter("All Agents");
    setCsatMonthPage(0);

    const start = dayjs(newDateRange.startDate);
    const end = dayjs(newDateRange.endDate);
    const today = dayjs().startOf("day");

    let range = "custom";
    if (start.isSame(today, "day") && end.isSame(today, "day")) range = "today";
    else if (start.isSame(today.subtract(1, "day"), "day") && end.isSame(today.subtract(1, "day"), "day")) range = "yesterday";
    else if (start.isSame(today.subtract(6, "day"), "day") && end.isSame(today, "day")) range = "7d";
    else if (start.isSame(today.subtract(29, "day"), "day") && end.isSame(today, "day")) range = "30d";
    else if (start.isSame(today.subtract(12, "month").add(1, "day"), "day") && end.isSame(today, "day")) range = "1y";

    await Promise.all([
      dispatch(getAnalyticsAgentsStats({ range, startDate: newDateRange.startDate, endDate: newDateRange.endDate, group_id: selectedGroupId })),
      dispatch(getAgentsChatsStats({ range, startDate: newDateRange.startDate, endDate: newDateRange.endDate, group_id: selectedGroupId })),
      dispatch(getAgentsTicketsStats({ range, startDate: newDateRange.startDate, endDate: newDateRange.endDate, group_id: selectedGroupId })),
      dispatch(getAgentsCsatStats({ range, startDate: newDateRange.startDate, endDate: newDateRange.endDate, agent_id: undefined, group_id: selectedGroupId })),
      dispatch(getAgentsAvailabilityStats({ range, startDate: newDateRange.startDate, endDate: newDateRange.endDate, group_id: selectedGroupId })),
    ]);
  };

  // Charts dataconst showTrendForRange = currentRange === '7d';

  const showTrendForRange = currentRange === '7d';
  const dataArray = [
    {
      title: 'Total Agents',
      number: analyticsAgentsData?.data?.metrics?.totalAgents?.value || 0,
      textPercentage: showTrendForRange ? analyticsAgentsData?.data?.metrics?.totalAgents?.change || 0 : null,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: 'Avg Chats per Agent',
      number: analyticsAgentsData?.data?.metrics?.avgChatsPerAgent?.value || 0,
      textPercentage: showTrendForRange ? analyticsAgentsData?.data?.metrics?.avgChatsPerAgent?.change || 0 : null,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: 'Avg Tickets per Agent',
      number: analyticsAgentsData?.data?.metrics?.avgTicketsPerAgent?.value || 0,
      textPercentage: showTrendForRange ? analyticsAgentsData?.data?.metrics?.avgTicketsPerAgent?.change || 0 : null,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
      isText: false,
      isTimeValue: false,
      isPercentage: false,
    },
    {
      title: 'Average CSAT',
      number: analyticsAgentsData?.data?.metrics?.avgCsat?.value || 0,
      textPercentage: showTrendForRange ? analyticsAgentsData?.data?.metrics?.avgCsat?.change || 0 : null,
      value: showTrendForRange ? 'vs last week' : undefined,
      showTrend: showTrendForRange,
      isText: false,
      isTimeValue: false,
      isPercentage: true,
    },
    {
      title: 'Busiest Agent',
      number: analyticsAgentsData?.data?.metrics?.busiestAgent?.name || '  -   ',
      value: undefined,
      textPercentage: null,
      showTrend: false,
      isText: true,
      isTimeValue: false,
      isPercentage: false,
    },
  ];

  const agentTabs = ['Agents Chats', 'Agents Tickets'];
  const chartData = useMemo(() => {
    const apiData = activeTab === "Agents Chats" ? agentsChatsData : agentsTicketsData;
    const fn = activeTab === "Agents Chats" ? transformAgentsChatsData : transformAgentsTicketsData;
    return apiData?.data ? fn(apiData.data, agentKeyMap) : [];
  }, [activeTab, agentsChatsData, agentsTicketsData, agentKeyMap]);

  // Update the getHeatmapData function (around line 536) to wrap all return statements:
  const getHeatmapData = () => {
    const filterLower = activeTimeFilter.toLowerCase();

    // === Handle custom range intelligently based on date range ===
    if (filterLower === 'custom' && dateRange?.startDate && dateRange?.endDate) {
      const start = dayjs(dateRange.startDate);
      const end = dayjs(dateRange.endDate);
      const daysDiff = end.diff(start, 'day') + 1;

      console.log(`Custom range: ${daysDiff} days from ${start.format('MMM D')} to ${end.format('MMM D')}`);

      // Single day
      if (daysDiff === 1) {
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 1, false),
          agentsAvailabilityData
        );
      }

      // Weekly view (2-7 days) - show individual days
      if (daysDiff <= 7) {
        const apiData = agentsAvailabilityData?.data?.data || agentsAvailabilityData?.data;
        if (apiData && apiData.length > 0) {
          const numHours = 8;
          const actualGroups = apiData.length / numHours;
          if (apiData.length % numHours === 0) {
            return attachAgentCounts(
              transformAvailabilityData(agentsAvailabilityData, actualGroups, false),
              agentsAvailabilityData
            );
          }
        }
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, daysDiff, false),
          agentsAvailabilityData
        );
      }

      // Monthly view (8-31 days)
      if (daysDiff <= 31) {
        const apiData = agentsAvailabilityData?.data?.data || agentsAvailabilityData?.data;
        if (apiData && apiData.length > 0) {
          const numHours = 8;
          const actualGroups = apiData.length / numHours;
          if (apiData.length % numHours === 0) {
            return attachAgentCounts(
              transformAvailabilityData(agentsAvailabilityData, actualGroups, true),
              agentsAvailabilityData
            );
          }
        }
        const estimatedGroups = Math.ceil(daysDiff / 3);
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, estimatedGroups, true),
          agentsAvailabilityData
        );
      }

      // Extended view (32-90 days)
      if (daysDiff <= 90) {
        const apiData = agentsAvailabilityData?.data?.data || agentsAvailabilityData?.data;
        if (apiData && apiData.length > 0) {
          const numHours = 8;
          const actualGroups = apiData.length / numHours;
          if (apiData.length % numHours === 0) {
            return attachAgentCounts(
              transformAvailabilityData(agentsAvailabilityData, actualGroups, true),
              agentsAvailabilityData
            );
          }
        }
        const estimatedGroups = Math.ceil(daysDiff / 7);
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, estimatedGroups, true),
          agentsAvailabilityData
        );
      }

      // Yearly view (91-365 days)
      if (daysDiff <= 365) {
        const months = Math.ceil(daysDiff / 30);
        const numMonths = Math.min(12, months);
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, numMonths, true),
          agentsAvailabilityData
        );
      }

      // Multi-year view (365+ days)
      return attachAgentCounts(
        transformAvailabilityData(agentsAvailabilityData, 12, true),
        agentsAvailabilityData
      );
    }

    // === Handle standard time filters ===
    switch (filterLower) {
      case 'today':
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 1, false),
          agentsAvailabilityData
        );
      case 'yesterday':
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 1, false),
          agentsAvailabilityData
        );
      case '7 days':
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 7, false),
          agentsAvailabilityData
        );
      case '30 days':
      case 'monthly':
      case '365d':
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 10, true),
          agentsAvailabilityData
        );
      case '12 months':
      case 'annually':
        return attachAgentCounts(
          transformAvailabilityData(agentsAvailabilityData, 12, true),
          agentsAvailabilityData
        );
      default:
        return buildEmptyAvailabilityGrid(
          ['12am', '3am', '6am', '9am', '12pm', '3pm', '6pm', '9pm'],
          7,
          false
        );
    }
  };

  const heatmapData = getHeatmapData();


  return (
    <div className="Dashboard_Main_Containerxx">
      <MainHeaderAnalytics
        title="Agents"
        dateRange={dateRange}
        onDateChange={handleDateChange}
        exportSectionOptions={exportSectionOptions}
        exportSection={exportSection}
        onExportSectionChange={setExportSection}
        onExport={handleExport}
        exportLoading={exportLoading}
        selectedGroupId={selectedGroupId}
        onGroupChange={setSelectedGroupId}
      />

      <OverViewChart data={dataArray} loading={dataLoading} />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 5, mb: 3 }}>
        <p style={{ margin: 0 }} className="overview_main">All Stats</p>
        <TabNavigation tabs={["Agents Chats", "Agents Tickets"]} activeTab={activeTab} onTabChange={setActiveTab} />
      </Box>

      <div style={{ marginBottom: "32px" }}>
        <BarChart
          data={chartData}
          selectedTab={activeTab}
          activeTimeFilter={activeTimeFilter}
          loading={dataLoading}
          agentNames={globalAgentNames}
          nameToColor={nameToColor}
        />
      </div>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, mb: 4, padding: 0, margin: 0 }}>
        <AgentsHeatmap timeFilter={activeTimeFilter} data={heatmapData} loading={availabilityLoading} />

        <Card elevation={0} sx={{ minWidth: 0, width: "100%", maxWidth: "100%", borderRadius: "8px", border: "1px solid #E5E9EC", background: "#FFF", flexGrow: { md: 1 }, flex: { md: "1 1 0" } }}>
          <CardContent sx={{ p: '24px' }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", }}>
              <Typography variant="h6" sx={{
                color: '#061522',
                fontFamily: 'Inter',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '18px',
              }}>
                Agent CSAT
              </Typography>
              {/* <Select
                value={csatAgentFilter}
                onChange={(e) => handleCsatAgentFilterChange(e.target.value)}
                size="small"
                disabled={csatLoading}
                IconComponent={Dropdown}
                sx={{
                  height: "40px",
                  minWidth: "152px",
                  maxWidth: "200px",
                  fontSize: "14px",
                  "& .MuiSelect-select": { fontSize: "14px", paddingLeft: "16px" },
                  "& .MuiInputBase-input": { paddingLeft: "16px !important" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D5DCE1 !important" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D5DCE1 !important" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#D5DCE1 !important" },
                  "& .MuiSelect-icon": { fontSize: "16px", position: "absolute", top: "50%", transform: "translateY(-50%)", right: "8px" },
                  "& .MuiSelect-iconOpen": { transform: "translateY(-50%) rotate(180deg)" },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: "1px solid #D5DCE1",
                      marginTop: "4px",
                      maxWidth: "250px",
                      padding: '0px 8px',
                      borderRadius: "8px",
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        padding: "8px 8px",
                        color: "#405261",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        "&:hover": { color: '#061522', borderRadius: "8px", background: "#F9FAFA !important" },
                        "&.Mui-selected": { color: '#061522', borderRadius: "8px", background: "#F9FAFA !important" },
                      },
                    },
                  },
                }}
              >
                {csatAgentOptions.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.name}
                    title={option.email || option.name}
                  >
                    {option.name}
                  </MenuItem>
                ))}
              </Select> */}

              {/* ARROWS NOW ALWAYS VISIBLE IN 12-MONTH VIEW */}
              {is12MonthView && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {/* Show LEFT arrow only when on Jul-Dec (page 1) */}
                  {csatMonthPage === 1 && (
                    <IconButton onClick={handlePrev} size="small">
                      <EastIcon sx={{ transform: "rotate(180deg)", color: "#148DFD" }} />
                    </IconButton>
                  )}


                  {/* Show RIGHT arrow only when on Jan-Jun (page 0) */}
                  {csatMonthPage === 0 && (
                    <IconButton onClick={handleNext} size="small">
                      <EastIcon sx={{ color: "#148DFD" }} />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>

            <Typography variant="body2" sx={{ color: "#405261", fontSize: 12, mb: 4, mt: '8px' }}>
              Agent satisfaction scores
            </Typography>

            <BarChart
              data={getCsatChartData}
              selectedTab="Agent CSAT"
              activeTimeFilter={activeTimeFilter}
              loading={csatLoading}
              csatAgentFilter={csatAgentFilter}
              agentNames={csatAgentNames}
              singleAgentColor={singleCsatAgentColor}
              nameToColor={nameToColor}
              is12MonthView={is12MonthView}
              csatMonthPage={csatMonthPage}
              hasDataInOtherPage={overallCsatHasData}
            />
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}

export default MainAnalyticsAgents;